/**
 * Uploading evidence for a deal requirement: the file (checked against the file policy by the field), the issue date
 * ("issued within N days" rules; defaults to the upload date on the server) and the expiry date (required for expiring
 * document types, after today). Waiving a requirement needs a reason.
 */
import { z } from 'zod';

import { M, zDateOnly, zRequiredString } from '@/business';

const optionalDate = () => z.union([z.literal(''), zDateOnly()]);

export const documentUploadSchema = (today: string, hasExpiry: boolean) =>
  z
    .object({ issuedOn: optionalDate(), expiresOn: optionalDate(), file: z.instanceof(File, { message: M.required }).nullable() })
    .superRefine((v, ctx) => {
      if (!v.file) ctx.addIssue({ code: 'custom', path: ['file'], message: M.required });
      if (v.issuedOn && v.issuedOn > today) ctx.addIssue({ code: 'custom', path: ['issuedOn'], message: 'validation.server.not_future' });
      if (hasExpiry && !v.expiresOn) ctx.addIssue({ code: 'custom', path: ['expiresOn'], message: M.required });
      if (v.expiresOn && v.expiresOn <= today) ctx.addIssue({ code: 'custom', path: ['expiresOn'], message: 'validation.server.expired' });
      if (v.expiresOn && v.issuedOn && v.expiresOn < v.issuedOn) ctx.addIssue({ code: 'custom', path: ['expiresOn'], message: 'validation.server.after_start' });
    });
export type DocumentUploadValues = z.infer<ReturnType<typeof documentUploadSchema>>;

/** Empty for every upload: a new version always states its own dates. */
export const emptyDocumentUpload = (): DocumentUploadValues => ({ issuedOn: '', expiresOn: '', file: null });

/** The attach-file body (without the upload id). Expiry is sent only for expiring types. */
export function toAttachRequest(v: DocumentUploadValues, hasExpiry: boolean): { issuedOn?: string; expiresOn?: string } {
  return { ...(v.issuedOn ? { issuedOn: v.issuedOn } : {}), ...(hasExpiry && v.expiresOn ? { expiresOn: v.expiresOn } : {}) };
}

export const waiveSchema = z.object({ reason: zRequiredString() });
export type WaiveValues = z.infer<typeof waiveSchema>;
